import { createContainer, createHook, createStore, defaults } from 'react-sweet-state';
import { first, get, isArray, find, includes } from 'lodash';

import { DataServiceFactory } from '../services/ApiDataService';
import { EnvironmentServiceFactory } from '../services/EnvironmentService';

// Integrate react-sweet-state with the redux dev tools.
defaults.devtools = true;

export function isPgm(state) {
    return state?.environment?.personality === 'PGM';
}

function load() {
    return ({ setState }, { environmentService }) => {
        // Use the environment service provided by the container if present.  Otherwise, use
        // the global environment service.
        const envService = environmentService || EnvironmentServiceFactory(DataServiceFactory());
        setState({
            initialized: false,
            environment: {},
        });

        envService.getEnvironment().then((environment) => {
            setState({
                initialized: true,
                environment,
            });
        });
    };
}

const EnvironmentStore = createStore({
    name: 'Environment',

    initialState: {
        initialized: false,
        environment: {},
    },

    actions: {
        load,
    },
});

export const EnvironmentStoreContainer = createContainer(EnvironmentStore, {
    onInit:
        () =>
        ({ dispatch }) => {
            dispatch(load());
        },
    onUpdate:
        () =>
        ({ dispatch }) => {
            dispatch(load());
        },
});

export const useEnvironment = createHook(EnvironmentStore);

export const useVersion = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.version,
});
export const useServerName = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.serverName,
});

export const usePortalLocation = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.portalLocation,
});

export const useSMTPEnabled = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.smtpEnabled,
});

export const useSessionRefreshRate = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.refreshRateMs,
});

export const usePasswordLength = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.passwordRules?.passwordMinLength,
});

export const usePasswordComplexity = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.passwordRules?.validatePasswordComplexity,
});

export const usePrimaryXMSLocation = createHook(EnvironmentStore, {
    selector: (state) => {
        const locations = state?.environment?.xmsLocation;
        if (isArray(locations) && locations.length > 0) {
            const matchingLocation = find(locations, (location) =>
                includes(get(location, 'host', ''), get(state, 'environment.portalLocation', 'portalUrlNotFound'))
            ); // default portalLocation to 'portalUrlNotFound' instead of empty string so includes returns false
            return get(matchingLocation, 'host', get(first(locations), 'host', ''));
        }

        return '';
    },
});

export const usePersonality = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.personality,
});

export const useIsPgm = createHook(EnvironmentStore, {
    selector: (state) => isPgm(state),
});
export const usePulseSoftwareVersion = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.pulseSoftwareVersion,
});

export const useMaxMind = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.maxMind,
});

export const useIsIpmDisabled = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.disabledIPM,
});

export const useIsSEMEnabled = createHook(EnvironmentStore, {
    // selector: (state) => state?.environment?.semLicenseCount > 0,
    selector: () => true,
});

export const useAreAnnotationsEnabled = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.annotationsEnabled,
});

export const useActiveStandbyRole = createHook(EnvironmentStore, {
    selector: (state) => state?.environment?.activeStandbyRole,
});

export default useEnvironment;
